import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext
} from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Button, Paper, Radio, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Header } from '../../components/Header';
import { ModalLoading } from '../../components/ModalLoading';
import { ModalMaterialRejection } from './components/ModalMaterialRejection';
import { ModalDisclaimerForDamages } from './components/ModalDisclaimerForDamages';
import { parsePhone } from '../../helpers/parseValues';
import { useAuth } from '../../hooks/Auth';
import { useModal } from '../../hooks/Modal';
import { usePickup } from '../../hooks/Pickup';
import { useManager } from '../../hooks/Manager';
import {
  Container,
  Content,
  Route,
  ProfileCollectTitle,
  ProfileCollectText,
  StackItem,
  RadioWrapper,
  TitlePage,
  CustomStack
} from './styled';

import { placa } from '../../assets/placa';

import { CollectTypeContext } from '../CollectType';

export const ProfileCollectContext = createContext();

const ProfileCollect = () => {
  const { user, userAddressList } = useAuth();
  const { openModal, closeModal, openedModal } = useModal();
  const {
    branchParameterizationId,
    enableDisclaimerForDamages,
    cancelDefaultPickup,
    cancelReversePickup
  } = usePickup();
  const { getIncompatibleGoodsConfiguration, getIncompatibleGoods } =
    useManager();

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const [radio, setRadio] = useState('');
  const [protocolCollectCanceled, setProtocolCollectCanceled] = useState('');
  const [isLoadingParams, setIsLoadingParams] = useState(true);
  const [incompatibleGoodsList, setIncompatibleGoodsList] = useState([]);
  const [incompatibleGoodsConfiguration, setIncompatibleGoodsConfiguration] =
    useState([]);

  const history = useHistory();

  const handleChangeRadio = (event) => {
    setRadio(event.target.value);
  };

  const handleReject = async () => {
    openModal('modalLoading');

    const collectType = localStorage.getItem('@Braspress:collectType');

    const address = {
      zipPostalCode: null,
      streetAddress: null,
      streetAddressNumber: null,
      additionalInformation: null,
      suburb: null,
      city: null,
      state: null,
      country: null
    };
    const contact = {
      name: null,
      email: null,
      phoneNumber: null,
      extension: null
    };

    if (userAddressList.length > 0) {
      address.zipPostalCode = parsePhone(userAddressList[0].zipPostalCode);
      address.streetAddress = userAddressList[0].streetAddress;
      address.streetAddressNumber = userAddressList[0].streetAddressNumber;
      address.additionalInformation = userAddressList[0].additionalInformation;
      address.suburb = userAddressList[0].suburb;
      address.city = userAddressList[0].city;
      address.state = userAddressList[0].state;
      address.country = userAddressList[0].country;
      contact.name = userAddressList[0].contactName;
      contact.email = userAddressList[0].contactEmail;
      contact.phoneNumber = userAddressList[0].contactNumber;
      contact.extension = userAddressList[0].contactExtension;
    }

    try {
      if (collectType && collectType === 'defaultCollect') {
        const response = await cancelDefaultPickup({
          data: {
            pickup: {
              requesterCorporation: {
                requesterType: 'sender',
                document: String(user.document),
                name: user.name,
                address,
                contactList: [contact]
              },
              shipmentList: [
                {
                  transportTypeId: null,
                  freightTypeId: null,
                  senderCorporation: {
                    document: String(user.document),
                    name: user.name,
                    address,
                    contactList: [contact]
                  },
                  receiverCorporation: null,
                  shipmentAvailability: null,
                  shipmentPackageList: null,
                  taxInformation: null
                }
              ],
              pickupCancelType: 'Material Incompatível'
            }
          }
        });

        if (response?.status === 200) {
          setProtocolCollectCanceled(response.data.data.protocol);
        }
      } else if (collectType && collectType === 'reverseCollect') {
        const response = await cancelReversePickup({
          data: {
            pickup: {
              requesterCorporation: {
                document: String(user.document),
                name: user.name,
                address,
                contactList: [contact]
              },
              shipmentList: [
                {
                  transportTypeId: null,
                  freightTypeId: null,
                  senderCorporation: null,
                  receiverCorporation: {
                    document: String(user.document),
                    name: user.name,
                    address,
                    contactList: [contact]
                  },
                  shipmentAvailability: null,
                  shipmentPackageList: null,
                  taxInformation: null
                }
              ],
              pickupCancelType: 'Material Incompatível'
            }
          }
        });

        if (response?.status === 200) {
          setProtocolCollectCanceled(response.data.data.protocol);
        }
      }

      openModal('modalMaterialRejection');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const handleConfirm = () => {
    const collectType = localStorage.getItem('@Braspress:collectType');

    if (collectType && collectType === 'defaultCollect') {
      if (enableDisclaimerForDamages) {
        openModal('modalDisclaimerForDamages');
      } else {
        history.push('/acesso-rapido/tipo-de-solicitante');
      }
    } else if (collectType && collectType === 'reverseCollect') {
      history.push('/acesso-rapido/pedido-de-coleta/reversa');
    }
  };

  useEffect(() => {
    if (!branchParameterizationId) {
      history.push('/acesso-rapido/tipo-de-coleta');
    }
  }, []);

  const getResultIncompatibleGoods = useCallback(async () => {
    openModal('modalLoading');

    try {
      const rsp = await getIncompatibleGoodsConfiguration({
        branchParameterizationId
      });

      if (rsp?.length > 0) {
        setIncompatibleGoodsConfiguration(rsp);
      }

      const response = await getIncompatibleGoods();

      if (response?.length > 0) {
        setIncompatibleGoodsList(response);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error);
    } finally {
      closeModal();
      setIsLoadingParams(false);
    }
  }, []);

  useEffect(() => {
    getResultIncompatibleGoods();
  }, []);

  const filteredIncompatibleGoodsConfiguration =
    incompatibleGoodsConfiguration.filter((value) => value.isVisible === true);

  const contextValues = {
    setRadio,
    protocolCollectCanceled,
    incompatibleGoodsList,
    incompatibleGoodsConfiguration
  };

  return (
    <ProfileCollectContext.Provider value={contextValues}>
      <Container>
        {/* <Header /> */}
        <Content>
          <Box margin="0 40px" paddingBottom="24px">
            {/* <Route>
              {!maxSM && (
                <>
                  Home {'>'} Acesso Rápido {'>'} Pedido de Coleta
                </>
              )}

              {!!user && (
                <Stack
                  direction="row"
                  spacing={2}
                  style={{
                    alignItems: 'center',
                    marginLeft: maxSM && 'auto'
                  }}
                >
                  <p>{user.name}</p>
                  <Button
                    type="button"
                    variant="contained"
                    color="secondary"
                    data-testid="signOutButton"
                    onClick={signOut}
                    size="small"
                  >
                    Sair
                  </Button>
                </Stack>
              )}
            </Route> */}

            <Box
              marginTop={4}
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
            >
              <TitlePage>
                PEDIDO DE COLETA - PERFIL
                <hr />
                <span>Confira a Lista de Materiais Incompatíveis</span>
              </TitlePage>

              <Paper
                elevation={3}
                style={{
                  width: '100%',
                  maxWidth: '840px',
                  padding: '16px 32px'
                }}
              >
                <CustomStack
                  columnBreak={
                    filteredIncompatibleGoodsConfiguration.length >= 20 &&
                    !maxSM
                  }
                >
                  {filteredIncompatibleGoodsConfiguration.map((item, index) => {
                    return incompatibleGoodsList.map(
                      (valor) =>
                        item.isVisible === true &&
                        item.incompatibleGoodsId ===
                          valor.incompatibleGoodsId && (
                          <StackItem key={valor.incompatibleGoodsId}>
                            <img
                              src={valor.url}
                              alt={valor.description}
                              title={valor.description}
                            />
                            <p>{valor.description}</p>
                          </StackItem>
                        )
                    );
                  })}
                </CustomStack>
              </Paper>

              <Stack spacing={1} width="100%" maxWidth="840px">
                <div>
                  <strong>
                    Declaro que a mercadoria entregue a transporte não se
                    enquadra na relação de material incompatível.
                    <br />
                    Estou ciente que a inexatidão das informações prestadas
                    poderá ensejar penalizações e multas de minha
                    responsabilidade.
                  </strong>
                </div>

                <Stack direction="row" spacing={1}>
                  <RadioWrapper>
                    <Radio
                      name="product-type"
                      id="product-type-confirmation"
                      value="confirm"
                      data-testid="confirmRadioInput"
                      checked={radio === 'confirm'}
                      onChange={handleChangeRadio}
                      onClick={handleConfirm}
                    />
                    <label htmlFor="product-type-confirmation">Sim</label>
                  </RadioWrapper>
                  <RadioWrapper>
                    <Radio
                      name="product-type"
                      id="product-type-rejection"
                      value="reject"
                      data-testid="rejectRadioInput"
                      checked={radio === 'reject'}
                      onChange={handleChangeRadio}
                      onClick={handleReject}
                    />
                    <label htmlFor="product-type-rejection">Não</label>
                  </RadioWrapper>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Content>
      </Container>

      {openedModal === 'modalLoading' && <ModalLoading />}
      {openedModal === 'modalDisclaimerForDamages' && (
        <ModalDisclaimerForDamages />
      )}
      {openedModal === 'modalMaterialRejection' && <ModalMaterialRejection />}
    </ProfileCollectContext.Provider>
  );
};

export default ProfileCollect;
